import React, { useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';
import { idxMap } from 'helpers/styleMapIdx';

import { Icon } from 'components/shared/Icon';
import { Tag } from 'components/shared/Tag';
import { Typography } from 'components/shared/Typography';

import { Tooltip } from 'components/Tooltip';

import { ErrorDetails } from '../ErrorDetails';
import { SlowDetails } from '../SlowDetails';

import CatalogContextBlock from '../../CatalogContextBlock';
import { getAnalyticsEventBaseName } from '../../analyticsEventUtils';
import { omitCatalogContextLines } from '../../catalogContextDisplay';
import { formatAnalyticsEventDisplay } from '../../formatAnalyticsEventLabel';
import {
  EVENT_KIND_ICONS,
  IDLE_EXIT_EVENT_ICON,
  buildDeveloperReport,
  formatOffset,
  getErrorDetails,
  getJourneyEventElementId,
  getSlowEventDetails,
  hasErrorDetailsCard,
  isEventSubtitleCoveredByDetails,
  type SessionEvent,
} from '../../TimelineSection.helpers';
import { useJourneyStudentId, useJourneyTechMode } from '../../journeyTechMode';

import styles from './SessionEventRow.module.scss';

const KIND_VARIANTS = {
  action: styles.kindAction,
  server: styles.kindServer,
  goal: styles.kindGoal,
  error: styles.kindError,
  warning: styles.kindWarning,
  slow: styles.kindWarning,
  rage: styles.kindWarning,
  exit: styles.kindExit,
};

const API_EVENT_NAMES = new Set(['API Error', 'Slow Request']);
const NO_HIDDEN_CATALOG_LINES = { course: false, lesson: false };
const NO_FOLDED_EVENTS: SessionEvent[] = [];

const SessionEventRow = ({
  highlighted,
  hideCatalogContext = false,
  hideCatalogLines = NO_HIDDEN_CATALOG_LINES,
  appVersion,
  studentEmail,
  event,
  title: titleOverride = null,
  titleNote = null,
  repeatCount = 1,
  foldedEvents = NO_FOLDED_EVENTS,
}: {
  highlighted: boolean;
  /** The row repeats its screen's lesson / module: no catalog block and no names in the subtitle. */
  hideCatalogContext?: boolean;
  /** Lines of the catalog block its chapter header already says (course line, lesson name). */
  hideCatalogLines?: { course: boolean; lesson: boolean };
  appVersion?: string | null;
  studentEmail?: string | null;
  event: SessionEvent;
  /** Title of a collapsed row: «Решил задачу 1 из 5», «Открыл задачу 4 из 5 — не решил». */
  title?: string | null;
  /** Short note after the title: «за 49 с», «решал 2 мин 50 с». */
  titleNote?: string | null;
  /** Identical rows folded into this one: «×3». */
  repeatCount?: number;
  /** Events of those folded rows (their request ids go to the developer block). */
  foldedEvents?: SessionEvent[];
}) => {
  const { i18n } = useTranslation();
  const techMode = useJourneyTechMode();
  const studentId = useJourneyStudentId();
  const display = useMemo(
    () =>
      formatAnalyticsEventDisplay(event.eventName, event.properties, {
        studentEmail,
        platform: event.platform,
        omitCatalog: hideCatalogContext,
      }),
    [event, studentEmail, hideCatalogContext, i18n.language],
  );
  const title = titleOverride ?? display.title;
  const isErrorLike = event.kind === 'error' || event.kind === 'warning';
  const errorDetails = useMemo(
    () =>
      isErrorLike
        ? getErrorDetails(event, { appVersion, title, foldedEvents })
        : null,
    [event, appVersion, title, isErrorLike, foldedEvents, i18n.language],
  );
  const catalogContext = hideCatalogContext
    ? null
    : omitCatalogContextLines(display.catalogContext, hideCatalogLines);
  const slowDetails = useMemo(
    () => (event.kind === 'slow' ? getSlowEventDetails(event) : null),
    [event, i18n.language],
  );
  const isIdleExit =
    event.kind === 'exit' && event.properties?.reason === 'idle_timeout';
  const showErrorCard =
    errorDetails != null && hasErrorDetailsCard(errorDetails);
  const showSubtitle =
    Boolean(display.subtitle) &&
    !isEventSubtitleCoveredByDetails(
      event,
      showErrorCard ? errorDetails.summaryRows : [],
      slowDetails,
    );
  /* Error cards carry the raw values in their own developer block. */
  const showTechnicalDetails =
    techMode && Boolean(display.technicalDetails) && !showErrorCard;

  const getDeveloperReport = () =>
    buildDeveloperReport({
      event,
      title,
      studentId,
      appVersion,
      foldedEvents,
    });

  return (
    <div
      id={getJourneyEventElementId(event.index)}
      className={cn(
        styles.row,
        idxMap(KIND_VARIANTS as Record<string, string>, event.kind),
        { [styles.rowHighlighted]: highlighted },
      )}
    >
      <Typography
        className={styles.offset}
        component="span"
        variant="body3"
        mode="compact"
      >
        {formatOffset(event.offsetMs)}
      </Typography>
      <span className={styles.iconCircle}>
        <Icon
          className={styles.icon}
          name={
            isIdleExit ? IDLE_EXIT_EVENT_ICON : EVENT_KIND_ICONS[event.kind]
          }
        />
      </span>
      <div className={styles.body}>
        <div className={styles.titleRow}>
          <Typography
            className={styles.title}
            variant="body2"
            weight="medium"
            mode="compact"
          >
            {title}
          </Typography>
          {titleNote ? (
            <Typography
              className={styles.titleNote}
              component="span"
              variant="body3"
              mode="compact"
            >
              {titleNote}
            </Typography>
          ) : null}
          {repeatCount > 1 ? (
            <Tooltip
              text={getTranslation('student_page.journey.log.repeat_hint', {
                count: repeatCount,
              })}
              triggerElement={
                <Tag
                  className={styles.tagNeutral}
                  textClassName={styles.tagNeutralText}
                >
                  {getTranslation('student_page.journey.log.repeat', {
                    count: repeatCount,
                  })}
                </Tag>
              }
            />
          ) : null}
          {techMode && event.source === 'server' ? (
            <Tooltip
              text={getTranslation('student_page.journey.log.tag_server_hint')}
              triggerElement={
                <Tag variant="info">
                  {getTranslation('student_page.journey.log.tag_server')}
                </Tag>
              }
            />
          ) : null}
          {techMode &&
          API_EVENT_NAMES.has(getAnalyticsEventBaseName(event.eventName)) ? (
            <Tag
              className={styles.tagNeutral}
              textClassName={styles.tagNeutralText}
            >
              {getTranslation('student_page.journey.log.tag_api')}
            </Tag>
          ) : null}
        </div>
        {showSubtitle ? (
          <Typography
            className={styles.subtitle}
            variant="body3"
            mode="compact"
          >
            {display.subtitle}
          </Typography>
        ) : null}
        {showTechnicalDetails ? (
          <Typography
            className={styles.technicalDetails}
            variant="body3"
            mode="compact"
          >
            {display.technicalDetails}
          </Typography>
        ) : null}
        {catalogContext ? (
          <CatalogContextBlock catalogContext={catalogContext} />
        ) : null}
        {showErrorCard ? (
          <ErrorDetails
            content={errorDetails}
            techMode={techMode}
            getDeveloperReport={getDeveloperReport}
            tone={event.kind === 'warning' ? 'warning' : 'error'}
          />
        ) : null}
        {slowDetails ? (
          <SlowDetails details={slowDetails} techMode={techMode} />
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(SessionEventRow) as any;
